import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { ListFooter } from "@/components/shared/list-footer";
import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import {
  ArrowLeftIcon,
  Columns3Icon,
  Download,
  FilterIcon,
  Upload,
  X,
} from "lucide-react";
import { useMutations } from "./utils/react-hook/use-mutations";
import { ReactForm, ReactTable, ReactTableSkelton } from "./utils";
import { Fragment } from "react/jsx-runtime";
import ErrorFallback from "@/components/shared/error-fallback";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { CustomErrorPopup } from "@/components/shared/custom-error-popup";
import { useUserInfo } from "@/global-data/use-user-info";

type UomMappingProps = {
  isBranchLevel: boolean;
  onLevelChange: (value: boolean) => void;
};

export const UomMapping = ({
  isBranchLevel,
  onLevelChange,
}: UomMappingProps) => {
  const {
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    skeltonLoading,
    tableColumn,
    setTableColumn,
    companyData,
    // retailerList,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    retailerDropdownList,
    errorMessage,
    closeErrorMessage,
    exportDocument,
    importDocument,
    sncErpUomList,
    sortingHandler,
    sortBy,
    orderBy,
    loadSNCustomer,
    sncCustomerList,
  } = useMutations();

  const {
    tableHeader,
    table,
    tableKeys,
    // toggleSelection,
    // rowSelection,
    onResize,
    onSorting,
    // loading,
  } = useTable({
    data: listData,
    view: [
      "uom",
      "po_uom_qty",
      "erp_uom",
      "conversion_rate",
      "retailer_id",
      "retailers",
      "customer_branches",
      "sku_code",
      "modified_by",
    ],
    verifyKey: "uom-mapping-branch-level",
    visible: tableColumn,
    avoid: [""],
    columnSize: {
      erp_uom: 90,
      retailer_id: 200,
      conversion_rate: 125,
      po_uom_qty: 125,
      uom: 100,
      customer_branches: 220,
      sku_code: 110,
      modified_by: 105,
    },
  });

  const { activePermission } = useUserInfo();
  return (
    <Fragment>
      {formPopupOpen ? (
        <div className="flex flex-col gap-5">
          <ListPageTopBar
            title={
              activeRecordData ? "Update UOM Mapping" : "Create UOM Mapping"
            }
          >
            <Button
              variant={"action"}
              onClick={() => {
                onFormOpenChange(false);
                closeErrorMessage();
              }}
            >
              <ArrowLeftIcon /> Back
            </Button>
          </ListPageTopBar>
          <Card className="pt-6">
            <ReactForm
              formData={activeRecordData}
              onFormSubmit={onFormSubmit}
              onOpenChange={onFormOpenChange}
              open={formPopupOpen}
              companyData={companyData}
              retailerList={retailerDropdownList}
              sncErpUomList={sncErpUomList}
              loadSNCustomer={loadSNCustomer}
              sncCustomerList={sncCustomerList}
            />
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ListPageTopBar title="UOM Mapping">
            <div className="flex items-center gap-2 mr-2">
              <span className="text-sm font-medium">Branch Level</span>
              <Switch checked={isBranchLevel} onCheckedChange={onLevelChange} />
            </div>
            <Button
              variant={"action"}
              disabled={activePermission?.create !== true}
              className="hidden relative cursor-pointer"
            >
              <Download />
              Import
              <input
                type="file"
                onChange={(e) => importDocument(e?.target?.files?.[0] || null)}
                className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer"
              />
            </Button>
            <Button
              variant={"action"}
              className="hidden"
              onClick={() => exportDocument()}
            >
              <Upload /> Export
            </Button>

            <TableColumnsHandle
              tableKeys={tableKeys}
              tableColumn={tableColumn}
              setTableColumn={setTableColumn}
              onSorting={onSorting}
              labels={{
                erp_uom: "Erp UOM",
                retailer_id: "Retailer Name",
                uom: "Po UOM",
                modified_by: "Modified by",
                sku_code: "Sku Code",
                po_uom_qty: "Po uom qty",
                conversion_rate: "Conversion rate",
                customer_branches: "Customer / Branch",
              }}
            >
              <Columns3Icon /> Column
            </TableColumnsHandle>

            <ConditionRenderer condition={Object.keys(filter).length !== 0}>
              <>
                <Button variant={"action"} onClick={filterRequest}>
                  <FilterIcon /> Filter
                </Button>
                <Button variant={"action"} onClick={clearFilter}>
                  <X /> Clear Filter
                </Button>
              </>
            </ConditionRenderer>

            {activePermission?.create ? (
              <Button variant={"action"} onClick={() => onFormOpenChange(true)}>
                + Create
              </Button>
            ) : null}
          </ListPageTopBar>

          {skeltonLoading ? (
            <ReactTableSkelton />
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }) => (
                    <ErrorFallback
                      error={error}
                      errorInfo={error.stack || ""}
                      resetErrorBoundary={resetErrorBoundary}
                    />
                  )}
                >
                  <ReactTable
                    tableHeader={tableHeader}
                    onResize={onResize}
                    table={table}
                    permission={permission}
                    onAction={onAction}
                    // retailerList={retailerList}
                    filter={filter}
                    setFilter={setFilter}
                    listData={listData}
                    filterRequest={filterRequest}
                    sortingHandler={sortingHandler}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    sncCustomerList={sncCustomerList}
                  />
                  {table.length === 0 ? <RecordNotFound /> : null}
                </ErrorBoundary>
              </CardContent>
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <hr />
                <ListFooter
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                  page={paginationData.page}
                  perPage={paginationData.perPage}
                  totalItem={paginationData.totalItem}
                  className="px-5 py-5"
                  isEmpty={table.length === 0}
                />
              </ErrorBoundary>
            </Card>
          )}
        </div>
      )}
      <CustomErrorPopup
        title={errorMessage?.title}
        description={errorMessage?.description}
        open={errorMessage?.open}
        setOpen={closeErrorMessage}
      />
    </Fragment>
  );
};
